/**
 * Supply request file operations (maintenance + hygiene).
 * Mirrors donationRequestFilesService for land requests.
 *
 * Hygiene: GET /api/donation/hygiene/supply/get/files, PATCH delete
 * Supply (maintenance): GET /api/donations/supply/files/get, PATCH delete
 * Upload: POST /api/donation/request/fileUpload (uses supplyId as requestToken when supported)
 */
import axios, { AxiosHeaders, AxiosRequestHeaders } from "axios";
import { API_BASE_URL } from "../config";
import { getAuthToken } from "./authService";

const getBaseURL = () =>
  process.env.NODE_ENV === "development" ? "" : API_BASE_URL;

const http = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json" },
  validateStatus: (s) => s >= 200 && s < 300,
});

http.interceptors.request.use((config) => {
  const tok = getAuthToken?.();
  if (tok) {
    const h = (config.headers ?? {}) as AxiosRequestHeaders | AxiosHeaders;
    (h as any).Authorization = `Bearer ${tok}`;
    config.headers = h as any;
  }
  return config;
});

function filenameFromCD(header?: string | null) {
  if (!header) return undefined;
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
  return m ? decodeURIComponent(m[1]) : undefined;
}

export type SupplyRequestFileRow = {
  id: number;
  name: string;
  documentTypeId: number;
  documentTypeName?: string;
  attachmentId?: number;
  accessToken?: string;
  tokenDonorId?: string;
};

const DOC = { FinancialCapacity: 1, PowerOfAttorney: 2 } as const;

function parseFileList(data: unknown): SupplyRequestFileRow[] {
  if (Array.isArray(data)) return data as SupplyRequestFileRow[];
  const d = data as Record<string, unknown> | null;
  const arr = d?.data ?? d?.Data ?? d?.items ?? d?.Items ?? d?.files ?? d?.Files;
  return Array.isArray(arr) ? (arr as SupplyRequestFileRow[]) : [];
}

/** List files for hygiene supply request */
export async function listHygieneSupplyFiles(
  supplyId: number
): Promise<SupplyRequestFileRow[]> {
  const { data } = await http.get(
    "/api/donation/hygiene/supply/get/files",
    { params: { supplyId } }
  );
  return parseFileList(data);
}

/** List files for maintenance supply request */
export async function listSupplyFiles(
  supplyId: number
): Promise<SupplyRequestFileRow[]> {
  const { data } = await http.get("/api/donations/supply/files/get", {
    params: { supplyId },
  });
  return parseFileList(data);
}

/** Upload file - uses generic donation request upload with supplyId as requestToken */
export async function uploadSupplyRequestFile(input: {
  supplyId: number;
  fileName: string;
  documentType: number;
  description?: string;
  fileBase64: string;
}) {
  const { data } = await http.post(
    "/api/donation/request/fileUpload",
    {
      requestToken: String(input.supplyId),
      fileName: input.fileName,
      documentType: input.documentType,
      description: input.description ?? "",
      fileBase64: input.fileBase64,
    },
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
}

/** Download via downloadByDonor (shared with land requests) */
export async function downloadSupplyFile(
  f: SupplyRequestFileRow
): Promise<{ blob: Blob; filename: string }> {
  const tok = getAuthToken?.();
  const qs = new URLSearchParams({
    attachmentId: String(f.attachmentId ?? f.id),
    accessToken: f.accessToken ?? "",
    tokenDonorId: f.tokenDonorId ?? "",
  });
  const res = await fetch(
    `${API_BASE_URL}/api/donation/file/downloadByDonor?${qs.toString()}`,
    {
      method: "GET",
      headers: { Accept: "*/*", ...(tok ? { Authorization: `Bearer ${tok}` } : {}) },
    }
  );
  if (!res.ok) throw new Error(String(res.status));
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const filename =
    filenameFromCD(cd) ||
    f.name?.trim() ||
    (f.documentTypeId === DOC.FinancialCapacity
      ? "خطاب الملاءة المالية"
      : "صك التوكيل الشرعي");
  return { blob, filename };
}
